// ─────────────────────────────────────────────────────────────────────────
// Asesores IA (27-ago-2026) — consultas en vivo del equipo a dos
// especialistas de la empresa: ⚖️ Abogado laboral y 🧮 Contador auditor.
// Cualquier usuario (ej. Zayda) elige un asesor, pregunta y recibe
// respuesta inmediata con el contexto real de Binimeliz. Las conversaciones
// quedan guardadas por usuario y se pueden retomar.
// Backend: routes/consulta.js · services/consultores.js
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";

const ASESORES = {
  abogado_laboral: {
    emoji: "⚖️",
    nombre: "Abogado laboral",
    desc: "Contratos, vacaciones, permisos, despidos, DT",
  },
  contador_auditor: {
    emoji: "🧮",
    nombre: "Contador auditor",
    desc: "IVA, F29, remuneraciones, SII, libro",
  },
};

const DISCLAIMER =
  "Orientación generada con IA sobre la realidad de Binimeliz — no reemplaza asesoría profesional formal; las decisiones sensibles se validan con especialista humano.";

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12 },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 8,
    padding: "10px 12px", fontSize: 14, color: "#ddd", width: "100%",
    boxSizing: "border-box", outline: "none", resize: "none",
    fontFamily: "inherit", lineHeight: 1.4,
  },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  btn: {
    background: "#e30613", color: "#fff", border: "none", borderRadius: 8,
    padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
  btnGhost: {
    background: "transparent", color: "#aaa", border: "1px solid #333",
    borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer",
  },
};

const fmtFecha = v => {
  if (!v) return "";
  const d = new Date(v);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  const hora = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (mismoDia) return `hoy ${hora}`;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()} ${hora}`;
};

// ── Burbuja de mensaje ────────────────────────────────────────────────────
const Burbuja = ({ m, emoji, onFaq }) => {
  const esUsuario = m.rol === "usuario";
  return (
    <div style={{ display: "flex", justifyContent: esUsuario ? "flex-end" : "flex-start" }}>
      <div
        style={{
          maxWidth: "82%",
          padding: "10px 14px",
          borderRadius: 12,
          fontSize: 14,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          ...(esUsuario
            ? { background: "#2e2e2e", color: "#eee", borderBottomRightRadius: 4 }
            : {
                background: "#232323", color: "#ddd",
                border: "1px solid #e30613", borderBottomLeftRadius: 4,
              }),
          ...(m.sinRespuesta ? { opacity: 0.75, borderStyle: "dashed", border: "1px dashed #666" } : {}),
        }}
      >
        {!esUsuario && (
          <div style={{ fontSize: 11, fontWeight: 700, color: "#e30613", marginBottom: 4 }}>
            {emoji} Asesor
          </div>
        )}
        {m.texto}
        <div style={{ fontSize: 10, color: "#777", marginTop: 6, textAlign: esUsuario ? "right" : "left", display: "flex", gap: 10, justifyContent: esUsuario ? "flex-end" : "flex-start", alignItems: "center" }}>
          <span>{fmtFecha(m.ts)}{m.sinRespuesta ? " · sin respuesta aún" : ""}</span>
          {!esUsuario && onFaq && (
            <button onClick={onFaq} title="Guardar en preguntas frecuentes"
              style={{ background: "none", border: "none", color: "#f39c12", cursor: "pointer", fontSize: 11, padding: 0 }}>
              ⭐ A frecuentes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Indicador "escribiendo…" ─────────────────────────────────────────────
const Escribiendo = ({ emoji }) => {
  const [puntos, setPuntos] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setPuntos(p => (p % 3) + 1), 450);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ display: "flex", justifyContent: "flex-start" }}>
      <div style={{
        background: "#232323", border: "1px solid #e30613", borderRadius: 12,
        borderBottomLeftRadius: 4, padding: "10px 14px", fontSize: 13, color: "#999",
      }}>
        {emoji} escribiendo{".".repeat(puntos)}
      </div>
    </div>
  );
};

const Asesores = () => {
  useTitle("Asesores");
  const isMobile = useIsMobile();

  const [convs, setConvs] = useState([]);
  const [asesor, setAsesor] = useState(null);   // id del especialista activo
  const [conv, setConv] = useState(null);       // conversación cargada (null = chat nuevo)
  const [pregunta, setPregunta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pendienteLocal, setPendienteLocal] = useState(null); // burbuja optimista mientras responde
  const [aviso, setAviso] = useState(null);
  const [listaAbierta, setListaAbierta] = useState(false);    // desplegable móvil
  const finRef = useRef(null);

  const cargarLista = async () => {
    try {
      const r = await backend.get("consulta");
      setConvs(r.data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { cargarLista(); }, []);

  // Preguntas frecuentes (27-ago): se generan desde el uso — ⭐ en una
  // respuesta la publica para todo el equipo; leerla no gasta consulta.
  const [faqs, setFaqs] = useState([]);
  const [faqAbierta, setFaqAbierta] = useState(null);
  useEffect(() => {
    backend.get("consulta/faq").then(r => setFaqs(r.data || [])).catch(() => {});
  }, [asesor]);

  const guardarFaq = async (indiceRespuesta) => {
    if (!conv?._id) return;
    try {
      await backend.post("consulta/faq", { consultaId: conv._id, indiceRespuesta });
      setAviso({ tipo: "ok", texto: "⭐ Guardada en preguntas frecuentes" });
      setTimeout(() => setAviso(null), 3000);
    } catch (e) { console.error(e); }
  };

  const abrirFaq = (f) => {
    setFaqAbierta(faqAbierta === f._id ? null : f._id);
    if (faqAbierta !== f._id) backend.post(`consulta/faq/${f._id}/vista`).catch(() => {});
  };

  const borrarFaq = async (f) => {
    if (!window.confirm(`¿Eliminar la pregunta frecuente “${f.pregunta}”?`)) return;
    try {
      await backend.delete(`consulta/faq/${f._id}`);
      setFaqs(prev => prev.filter(row => row._id !== f._id));
      if (faqAbierta === f._id) setFaqAbierta(null);
    } catch (e) {
      setAviso({ tipo: "error", texto: e?.response?.data?.error || "No se pudo eliminar la pregunta frecuente" });
    }
  };

  const mensajes = [
    ...(conv?.mensajes || []),
    ...(pendienteLocal ? [pendienteLocal] : []),
  ];

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conv, pendienteLocal, enviando]);

  const nuevaConsulta = id => {
    setAsesor(id); setConv(null); setAviso(null); setPregunta("");
    setListaAbierta(false);
  };

  const abrirConv = async c => {
    setAviso(null); setListaAbierta(false);
    try {
      const r = await backend.get(`consulta/${c._id}`);
      setConv(r.data); setAsesor(r.data.especialista);
    } catch (e) {
      setAviso("No se pudo abrir la conversación.");
    }
  };

  const volver = () => { setAsesor(null); setConv(null); setAviso(null); };

  // Tras un 502 el backend guardó la pregunta igual: recargar para mostrarla
  // con su marca "sin respuesta" y ofrecer reintento.
  const recargarConv = async id => {
    try {
      const r = await backend.get(`consulta/${id}`);
      setConv(r.data);
    } catch (e) { console.error(e); }
  };

  const enviar = async () => {
    const texto = pregunta.trim();
    if (!texto || enviando) return;
    setEnviando(true); setAviso(null);
    setPendienteLocal({ rol: "usuario", texto, ts: new Date().toISOString() });
    setPregunta("");
    try {
      const r = conv
        ? await backend.post(`consulta/${conv._id}/mensaje`, { pregunta: texto })
        : await backend.post("consulta", { especialista: asesor, pregunta: texto });
      setConv(r.data);
      cargarLista();
    } catch (e) {
      const data = e?.response?.data || {};
      setAviso(data.error || "El asesor no está disponible, intente de nuevo.");
      const id = conv?._id || data.consultaId;
      if (id) { await recargarConv(id); cargarLista(); }
      else setPregunta(texto); // ni siquiera se alcanzó a guardar: no perder lo escrito
    }
    setPendienteLocal(null);
    setEnviando(false);
  };

  const reintentar = async () => {
    if (!conv || enviando) return;
    setEnviando(true); setAviso(null);
    try {
      const r = await backend.post(`consulta/${conv._id}/mensaje`, {});
      setConv(r.data);
      cargarLista();
    } catch (e) {
      setAviso(e?.response?.data?.error || "El asesor no está disponible, intente de nuevo.");
    }
    setEnviando(false);
  };

  const borrarConv = async c => {
    if (!window.confirm(`¿Eliminar la consulta "${c.titulo}"?`)) return;
    try {
      await backend.delete(`consulta/${c._id}`);
      if (conv?._id === c._id) { setConv(null); }
      cargarLista();
    } catch (e) {
      setAviso(e?.response?.data?.error || "No se pudo eliminar.");
    }
  };

  const alTeclear = e => {
    if (e.key === "Enter" && !e.shiftKey && !isMobile) {
      e.preventDefault();
      enviar();
    }
  };

  const ultimoEsPregunta =
    !enviando && conv?.mensajes?.length > 0 &&
    conv.mensajes[conv.mensajes.length - 1].rol === "usuario";

  // ── Lista de conversaciones (sidebar desktop / desplegable móvil) ──────
  const ListaConvs = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {convs.length === 0 && (
        <div style={{ fontSize: 13, color: "#777", padding: "6px 2px" }}>
          Aún no tiene consultas guardadas.
        </div>
      )}
      {convs.map(c => (
        <div
          key={c._id}
          onClick={() => abrirConv(c)}
          style={{
            ...S.card,
            padding: "8px 10px", cursor: "pointer", display: "flex",
            alignItems: "center", gap: 8,
            border: conv?._id === c._id ? "1px solid #e30613" : "1px solid #2e2e2e",
          }}
        >
          <span style={{ fontSize: 16 }}>{ASESORES[c.especialista]?.emoji || "❔"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, color: "#ddd", whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {c.titulo || "(sin título)"}
            </div>
            <div style={{ fontSize: 10, color: "#777" }}>{fmtFecha(c.updatedAt)}</div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); borrarConv(c); }}
            title="Eliminar consulta"
            style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 14, padding: 2 }}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );

  const Disclaimer = () => (
    <div style={{ fontSize: 10.5, color: "#777", lineHeight: 1.4, marginTop: 10, textAlign: "center" }}>
      {DISCLAIMER}
    </div>
  );

  // ── Vista selector (sin asesor elegido) ────────────────────────────────
  if (!asesor) {
    return (
      <div style={{ padding: isMobile ? 12 : 24, maxWidth: 900, margin: "0 auto" }}>
        <h2 style={{ color: "#eee", margin: "0 0 4px" }}>Asesores</h2>
        <div style={{ fontSize: 13, color: "#999", marginBottom: 18 }}>
          Consulte en vivo a los especialistas IA de la empresa.
        </div>

        <div style={{
          display: "grid", gap: 14,
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        }}>
          {Object.entries(ASESORES).map(([id, a]) => (
            <div
              key={id}
              onClick={() => nuevaConsulta(id)}
              style={{
                ...S.card, padding: 22, cursor: "pointer",
                display: "flex", flexDirection: "column", gap: 8,
              }}
            >
              <div style={{ fontSize: 38 }}>{a.emoji}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#eee" }}>{a.nombre}</div>
              <div style={{ fontSize: 13, color: "#999" }}>{a.desc}</div>
              <div style={{ fontSize: 13, color: "#e30613", fontWeight: 700, marginTop: 4 }}>
                Hacer una consulta →
              </div>
            </div>
          ))}
        </div>

        {faqs.length > 0 && (
          <>
            <div style={{ ...S.label, margin: "22px 0 8px" }}>Preguntas frecuentes del equipo</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {faqs.map(f => (
                <div key={f._id} style={{ ...S.card, padding: 0, overflow: "hidden" }}>
                  <div onClick={() => abrirFaq(f)}
                    style={{ padding: "11px 14px", cursor: "pointer", display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ flexShrink: 0 }}>{ASESORES[f.especialista]?.emoji || "⭐"}</span>
                    <span style={{ flex: 1, fontSize: 13, color: "#ddd", fontWeight: 600 }}>{f.pregunta}</span>
                    <button type="button" onClick={e => { e.stopPropagation(); borrarFaq(f); }} title="Eliminar pregunta frecuente"
                      style={{ background: "none", border: 0, color: "#e74c3c", cursor: "pointer", padding: "2px 5px" }}>🗑</button>
                    <span style={{ fontSize: 10, color: "#666", flexShrink: 0 }}>{faqAbierta === f._id ? "▲" : "▼"}</span>
                  </div>
                  {faqAbierta === f._id && (
                    <div style={{ borderTop: "1px solid #2e2e2e", padding: "12px 14px", fontSize: 13, color: "#bbb", lineHeight: 1.55, whiteSpace: "pre-wrap", background: "#1f1f1f" }}>
                      {f.respuesta}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ ...S.label, margin: "22px 0 8px" }}>Mis consultas anteriores</div>
        <ListaConvs />
        <Disclaimer />
      </div>
    );
  }

  // ── Vista chat ─────────────────────────────────────────────────────────
  const a = ASESORES[asesor];

  const chat = (
    <div style={{
      ...S.card, display: "flex", flexDirection: "column",
      height: isMobile ? "calc(100dvh - 200px)" : "calc(100vh - 200px)",
      minHeight: 380, overflow: "hidden",
    }}>
      {/* Cabecera del chat */}
      <div style={{
        padding: "10px 14px", borderBottom: "1px solid #2e2e2e",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>{a.emoji}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#eee" }}>{a.nombre}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {conv ? conv.titulo : "Nueva consulta"}
          </div>
        </div>
        {conv && (
          <button style={S.btnGhost} onClick={() => nuevaConsulta(asesor)}>＋ Nueva</button>
        )}
      </div>

      {/* Mensajes */}
      <div style={{
        flex: 1, overflowY: "auto", padding: 14,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {mensajes.length === 0 && !enviando && (
          <div style={{ fontSize: 13, color: "#888", textAlign: "center", marginTop: 30, lineHeight: 1.6 }}>
            {a.emoji} Escriba su pregunta abajo.<br />
            <span style={{ color: "#666" }}>{a.desc}.</span>
          </div>
        )}
        {mensajes.map((m, i) => (
          <Burbuja key={i} m={m} emoji={a.emoji}
            onFaq={conv?._id && m.rol === "asesor" && i < (conv?.mensajes?.length || 0) ? () => guardarFaq(i) : null} />
        ))}
        {enviando && <Escribiendo emoji={a.emoji} />}
        {ultimoEsPregunta && (
          <div style={{ textAlign: "center" }}>
            <button style={{ ...S.btnGhost, borderColor: "#e30613", color: "#e30613" }} onClick={reintentar}>
              ↻ Reintentar respuesta
            </button>
          </div>
        )}
        <div ref={finRef} />
      </div>

      {/* Aviso de error */}
      {aviso && (
        <div style={{
          margin: "0 14px 8px", padding: "8px 12px", borderRadius: 8,
          background: "#2a1215", border: "1px solid #5c2227",
          fontSize: 12.5, color: "#f1aeb5",
        }}>
          {aviso}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: 12, borderTop: "1px solid #2e2e2e", display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          rows={isMobile ? 2 : 2}
          style={{ ...S.input, flex: 1 }}
          placeholder={`Pregúntele al ${a.nombre.toLowerCase()}…`}
          value={pregunta}
          onChange={e => setPregunta(e.target.value)}
          onKeyDown={alTeclear}
          disabled={enviando}
        />
        <button
          style={{ ...S.btn, opacity: enviando || !pregunta.trim() ? 0.5 : 1 }}
          onClick={enviar}
          disabled={enviando || !pregunta.trim()}
        >
          Enviar
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ padding: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <button style={S.btnGhost} onClick={volver}>←</button>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#eee" }}>
            {a.emoji} {a.nombre}
          </div>
          <button style={S.btnGhost} onClick={() => setListaAbierta(v => !v)}>
            {listaAbierta ? "Cerrar" : "Historial"}
          </button>
        </div>
        {listaAbierta && (
          <div style={{ ...S.card, padding: 10, marginBottom: 10, maxHeight: 260, overflowY: "auto" }}>
            <ListaConvs />
          </div>
        )}
        {chat}
        <Disclaimer />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button style={S.btnGhost} onClick={volver}>← Asesores</button>
        <h2 style={{ color: "#eee", margin: 0, fontSize: 20 }}>{a.emoji} {a.nombre}</h2>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "start" }}>
        <div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {Object.entries(ASESORES).map(([id, x]) => (
              <button
                key={id}
                style={{
                  ...S.btnGhost, textAlign: "left",
                  borderColor: id === asesor ? "#e30613" : "#333",
                  color: id === asesor ? "#eee" : "#aaa",
                }}
                onClick={() => nuevaConsulta(id)}
              >
                {x.emoji} Nueva consulta — {x.nombre}
              </button>
            ))}
          </div>
          <div style={{ ...S.label, marginBottom: 8 }}>Mis consultas</div>
          <div style={{ maxHeight: "calc(100vh - 330px)", overflowY: "auto" }}>
            <ListaConvs />
          </div>
        </div>
        <div>
          {chat}
          <Disclaimer />
        </div>
      </div>
    </div>
  );
};

export default Asesores;
